// Catalog import review + finalize page.
//
// After ProcessCatalogImport streams to completion, the
// ImportDialog redirects here. The page lists every
// CatalogImportReview produced by the workflow, lets the
// procurement specialist accept / reject items, and then
// calls BatchReviewImportReviews + FinalizeCatalogImport
// to actually persist the products.

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { catalogClient } from "@/lib/catalog-client";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const searchSchema = z.object({
	// Mirrors omnios.v1.ImportDestination enum.
	destination: z.coerce.number().int().min(0).max(2).optional().default(0),
	supplier_party_id: z.string().optional(),
	competitor_id: z.string().optional(),
});

export const Route = createFileRoute("/catalog/import-reviews/$jobId")({
	validateSearch: (search) => searchSchema.parse(search),
	component: ImportReviewsPage,
});

const PAGE_SIZE = 1000;

function ImportReviewsPage() {
	const { jobId } = Route.useParams();
	const search = Route.useSearch();
	const destination = search.destination ?? 0;
	const supplierPartyId = search.supplier_party_id;
	const competitorId = search.competitor_id;

	const navigate = useNavigate();
	const qc = useQueryClient();
	const { t } = useTranslation();

	useBreadcrumbs([
		{ label: "Каталог", href: "/catalog" },
		{ label: "Проверка импорта" },
	]);

	const reviewsQuery = useQuery({
		queryKey: ["import-reviews", jobId],
		queryFn: () =>
			catalogClient.listImportReviews({
				jobId,
				page: { pageSize: PAGE_SIZE, pageToken: "" },
				search: "",
				status: "",
			}),
	});

	const reviews = reviewsQuery.data?.reviews ?? [];

	// Local checkbox state — accepted/rejected by id.
	// Default: all accepted (most-common procurement flow).
	const [accepted, setAccepted] = useState<Record<string, boolean>>({});
	useEffect(() => {
		if (reviews.length === 0) return;
		setAccepted((prev) => {
			const next = { ...prev };
			for (const r of reviews) {
				if (next[r.id] === undefined) next[r.id] = true;
			}
			return next;
		});
	}, [reviews]);

	const acceptedIds = useMemo(
		() => reviews.filter((r) => accepted[r.id]).map((r) => r.id),
		[reviews, accepted],
	);
	const rejectedIds = useMemo(
		() => reviews.filter((r) => accepted[r.id] === false).map((r) => r.id),
		[reviews, accepted],
	);

	// Supplier-mode only: archive products owned by this
	// supplier that aren't in this import. Off by default to
	// avoid surprising archive on partial re-imports.
	const [markDiscontinued, setMarkDiscontinued] = useState(false);

	const finalizeMut = useMutation({
		mutationFn: async () => {
			if (acceptedIds.length > 0) {
				await catalogClient.batchReviewImportReviews({
					ids: acceptedIds,
					action: "accept",
				});
			}
			if (rejectedIds.length > 0) {
				await catalogClient.batchReviewImportReviews({
					ids: rejectedIds,
					action: "reject",
				});
			}
			return catalogClient.finalizeCatalogImport({
				jobId,
				markDiscontinued: markDiscontinued && destination === 2,
				destination,
				supplierPartyId: supplierPartyId || undefined,
				competitorId: competitorId || undefined,
			});
		},
		onSuccess: (resp) => {
			toast.success(
				`Импорт завершён: создано ${resp.created}, обновлено ${resp.updated}` +
					(resp.discontinued > 0 ? `, снято ${resp.discontinued}` : "") +
					(resp.mapped > 0 ? `, сопоставлено ${resp.mapped}` : "") +
					(resp.zeroPrice > 0
						? `, ${t("import_review_zero_price", { count: resp.zeroPrice })}`
						: "") +
					(resp.priceSwings > 0
						? `, ${t("import_review_price_swings", { count: resp.priceSwings })}`
						: "") +
					(resp.categoryMisses > 0
						? `, ${t("import_review_category_misses", { count: resp.categoryMisses })}`
						: "") +
					(resp.codeConflicts > 0
						? `, ${t("import_review_code_conflicts", { count: resp.codeConflicts })}`
						: ""),
			);
			qc.invalidateQueries({ queryKey: ["import-reviews", jobId] });
			qc.invalidateQueries({ queryKey: ["products"] });
			if (supplierPartyId) {
				navigate({ to: "/parties/$id", params: { id: supplierPartyId } });
			} else {
				navigate({
					to: "/catalog/products",
					search: { owner_party_id: undefined },
				});
			}
		},
		onError: (err) => {
			toast.error(
				`Ошибка финализации: ${err instanceof Error ? err.message : "—"}`,
			);
		},
	});

	const acceptedCount = acceptedIds.length;
	const rejectedCount = rejectedIds.length;

	const allChecked = reviews.length > 0 && acceptedCount === reviews.length;
	const noneChecked = acceptedCount === 0 && rejectedCount === reviews.length;

	function setAll(value: boolean) {
		const next: Record<string, boolean> = {};
		for (const r of reviews) next[r.id] = value;
		setAccepted(next);
	}

	function destinationLabel() {
		switch (destination) {
			case 1:
				return "Конкурент";
			case 2:
				return "Поставщик";
			default:
				return "Внутренний каталог";
		}
	}

	return (
		<div className="h-full overflow-y-auto p-6 space-y-4">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/background-jobs">
						<ArrowLeft className="h-4 w-4 mr-1" />
						Назад
					</Link>
				</Button>
				<div className="flex-1">
					<h1 className="text-2xl font-semibold">Проверка импорта</h1>
					<p className="text-sm text-muted-foreground">
						Назначение: {destinationLabel()}
						{supplierPartyId
							? ` · поставщик ${supplierPartyId.slice(0, 8)}`
							: ""}
						{" · "}
						задание {jobId.slice(0, 8)}
					</p>
				</div>
			</div>

			{reviewsQuery.isLoading && (
				<Card className="p-8 flex items-center gap-2 text-muted-foreground">
					<Loader2 className="h-5 w-5 animate-spin" />
					Загрузка элементов проверки…
				</Card>
			)}

			{reviewsQuery.isError && (
				<Card className="p-8 text-destructive">
					Ошибка загрузки:{" "}
					{reviewsQuery.error instanceof Error
						? reviewsQuery.error.message
						: "—"}
				</Card>
			)}

			{reviewsQuery.isSuccess && reviews.length === 0 && (
				<Card className="p-8 text-muted-foreground text-center">
					Для этого задания нет элементов проверки.
				</Card>
			)}

			{reviews.length > 0 && (
				<>
					<Card className="p-3 flex items-center justify-between gap-4 sticky top-0 z-10 bg-card">
						<div className="text-sm text-muted-foreground">
							Всего: <b className="text-foreground">{reviews.length}</b>
							{" · "}принято: <b className="text-foreground">{acceptedCount}</b>
							{" · "}отклонено:{" "}
							<b className="text-foreground">{rejectedCount}</b>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							{destination === 2 && (
								<label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer pr-2">
									<Checkbox
										checked={markDiscontinued}
										onCheckedChange={(v) => setMarkDiscontinued(Boolean(v))}
									/>
									Снять отсутствующие с продажи
								</label>
							)}
							<Button
								size="sm"
								variant="outline"
								disabled={allChecked}
								onClick={() => setAll(true)}
							>
								Принять все
							</Button>
							<Button
								size="sm"
								variant="outline"
								disabled={noneChecked}
								onClick={() => setAll(false)}
							>
								Отклонить все
							</Button>
							<Button
								size="sm"
								disabled={
									finalizeMut.isPending ||
									(destination === 2 && !supplierPartyId) ||
									(destination === 1 && !competitorId)
								}
								onClick={() => finalizeMut.mutate()}
							>
								{finalizeMut.isPending ? (
									<>
										<Loader2 className="h-4 w-4 mr-1 animate-spin" />
										Завершение…
									</>
								) : (
									<>
										<CheckCircle2 className="h-4 w-4 mr-1" />
										Завершить импорт
									</>
								)}
							</Button>
						</div>
					</Card>

					<Card className="overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-10">
										<Checkbox
											checked={allChecked}
											onCheckedChange={(v) => setAll(Boolean(v))}
										/>
									</TableHead>
									<TableHead>Код</TableHead>
									<TableHead>Наименование</TableHead>
									<TableHead>Бренд</TableHead>
									<TableHead>Категория</TableHead>
									<TableHead className="text-right">Цена</TableHead>
									<TableHead className="text-right">
										{t("import_review_current_price")}
									</TableHead>
									<TableHead className="text-right">
										{t("import_review_delta")}
									</TableHead>
									<TableHead>Статус</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{reviews.map((r) => {
									const isAccepted = accepted[r.id] !== false;
									const newCents = Number(r.priceCents);
									const currentCents = Number(r.currentPriceCents);
									const deltaPct =
										newCents > 0 && currentCents > 0
											? ((newCents - currentCents) / currentCents) * 100
											: null;
									return (
										<TableRow key={r.id}>
											<TableCell>
												<Checkbox
													checked={isAccepted}
													onCheckedChange={(v) =>
														setAccepted((prev) => ({
															...prev,
															[r.id]: Boolean(v),
														}))
													}
												/>
											</TableCell>
											<TableCell className="font-mono text-xs">
												{r.productCode || "—"}
											</TableCell>
											<TableCell>{r.productName || "—"}</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{r.brand || "—"}
											</TableCell>
											<TableCell className="text-sm text-muted-foreground">
												{r.category || "—"}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{r.priceCents
													? `${(Number(r.priceCents) / 100).toFixed(2)} ${
															r.currency || "₽"
														}`
													: "—"}
											</TableCell>
											<TableCell className="text-right tabular-nums text-muted-foreground">
												{currentCents > 0
													? `${(currentCents / 100).toFixed(2)} ${
															r.currentPriceCurrency || "₽"
														}`
													: "—"}
											</TableCell>
											<TableCell
												className={`text-right tabular-nums ${
													deltaPct === null
														? "text-muted-foreground"
														: Math.abs(deltaPct) > 50
															? "text-red-600 font-semibold"
															: deltaPct > 0
																? "text-red-500"
																: "text-green-600"
												}`}
											>
												{deltaPct === null
													? "—"
													: `${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(1)}%`}
											</TableCell>
											<TableCell className="text-xs text-muted-foreground">
												{r.status || "pending"}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Card>
				</>
			)}
		</div>
	);
}
